import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Sparkles, Target, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, pct, shortDate } from "@/lib/format";

const BLANK = {
  code: "", name: "", objective: "", activity: "education", target_population: "", country: "IN",
  budget: 0, spent: 0, status: "active", start_date: "", end_date: "", indicators: [], projects: [],
};

export default function Programs() {
  const { can } = useAuth();
  const [programs, setPrograms] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [summary, setSummary] = useState("");
  const [claim, setClaim] = useState(null);
  const [indicator, setIndicator] = useState(null);

  const saveIndicator = async () => {
    setBusy("indicator");
    try {
      const body = { name: indicator.name, baseline: Number(indicator.baseline) || 0,
                     target: Number(indicator.target) || 0, result: Number(indicator.result) || 0,
                     unit: indicator.unit || "" };
      if (indicator.index === null) {
        await api.post(`/charity/programs/${indicator.code}/indicators`, body);
        toast.success(`${body.name} added`);
      } else {
        await api.put(`/charity/programs/${indicator.code}/indicators/${indicator.index}`, body);
        toast.success(`${body.name} updated`);
      }
      setIndicator(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const removeIndicator = async (code, index) => {
    setBusy("indicator");
    try {
      await api.delete(`/charity/programs/${code}/indicators/${index}`);
      toast.success("Indicator removed");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  const [funds, setFunds] = useState([]);

  useEffect(() => {
    api.get("/charity/funds").then((r) => setFunds(r.data)).catch(() => setFunds([]));
  }, []);

  const sendClaim = async () => {
    setBusy("claim");
    try {
      const { data } = await api.post(`/charity/programs/${claim.code}/manual-expenditure`, {
        amount: Number(claim.amount) || 0, reason: claim.reason, fund_id: claim.fund_id,
      });
      toast.success(`${data.request_ref} sent for approval`);
      setClaim(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const load = useCallback(() => api.get("/charity/programs").then((r) => setPrograms(r.data)), []);
  useEffect(() => { load().catch(() => setPrograms([])); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      await api.post("/charity/programs", form);
      toast.success("Programme created");
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const composeSummary = async () => {
    setBusy("ai");
    setSummary("");
    try {
      const figures = programs.map((p) =>
        `${p.name}: budget ${p.budget}, spent ${p.spent}, indicators ${(p.indicators || [])
          .map((i) => `${i.name} ${i.result}/${i.target}`).join("; ")}`).join(" | ");
      const { data } = await api.post("/ai/draft", {
        mode: "impact_summary",
        prompt: `Write a donor-facing impact lay-summary for ServeCOR Demo Foundation using these figures: ${figures}`,
      });
      setSummary(data.text || "");
      toast.success("Impact lay-summary generated");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  if (!programs) return <Loading label="Loading programmes" />;
  const budget = programs.reduce((a, p) => a + (p.budget || 0), 0);
  const spent = programs.reduce((a, p) => a + (p.spent_ledger ?? p.spent ?? 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-009 / SCR-025 · Programme & Impact"
        title="Funded outcomes, on budget, with evidence"
        description="Programmes, projects, budget-versus-actual and MEAL indicators against baselines and targets — plus an AI-composed donor lay-summary."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={composeSummary} disabled={busy === "ai"}
                    data-testid="compose-impact-summary-button" className="rounded-full border-violet-500/30 text-xs text-violet-700 dark:text-violet-300">
              {busy === "ai" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1.5 h-3 w-3" />}
              Compose impact summary
            </Button>
            {can("program", "write") && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-program-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Plus className="mr-1.5 h-4 w-4" /> New programme
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg border-border bg-card">
                  <DialogHeader><DialogTitle>Create a programme</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Code">
                      <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                             data-testid="program-code-input" className="bg-muted/40 font-mono" />
                    </F>
                    <F label="Activity">
                      <Input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })}
                             data-testid="program-activity-input" className="bg-muted/40 font-mono" />
                    </F>
                    <F label="Name" full>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                             data-testid="program-name-input" className="bg-muted/40" />
                    </F>
                    <F label="Objective" full>
                      <Textarea rows={2} value={form.objective}
                                onChange={(e) => setForm({ ...form, objective: e.target.value })}
                                data-testid="program-objective-input" className="bg-muted/40 text-xs" />
                    </F>
                    <F label="Budget">
                      <Input type="number" value={form.budget}
                             onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })}
                             data-testid="program-budget-input" className="bg-muted/40 font-mono" />
                    </F>
                    <F label="Target population">
                      <Input value={form.target_population}
                             onChange={(e) => setForm({ ...form, target_population: e.target.value })}
                             data-testid="program-population-input" className="bg-muted/40" />
                    </F>
                  </div>
                  <Button onClick={save} disabled={busy === "save" || !form.code} data-testid="program-save-button"
                          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create programme
                  </Button>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-program-count" label="Active programmes" value={programs.length} accent="sky" icon={Target} />
        <Kpi testId="kpi-program-budget" label="Committed budget" value={money(budget, "INR", true)} accent="violet" />
        <Kpi testId="kpi-program-spent" label="Deployed" value={money(spent, "INR", true)} accent="emerald"
             sub={`${pct(spent, budget)} of budget`} />
      </div>

      {summary && (
        <Panel testId="panel-impact-summary" kicker="AI lay-summary (GPT-5.5)" title="Donor impact narrative">
          <p className="whitespace-pre-wrap p-4 text-sm leading-relaxed text-foreground/80" data-testid="impact-summary-text">
            {summary}
          </p>
        </Panel>
      )}

      <div className="space-y-4">
        {programs.map((p) => (
          <Panel key={p.id} testId={`program-card-${p.code}`}
                 kicker={`${p.code} · ${p.activity} · ${p.country}`} title={p.name}
                 actions={<Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">{p.status}</Chip>}>
            <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[1.2fr_1fr]">
              <div className="space-y-3">
                <p className="text-xs leading-relaxed text-muted-foreground">{p.objective}</p>
                <div className="text-[11px] text-muted-foreground">
                  Target population: <span className="text-foreground/80">{p.target_population || "—"}</span>
                </div>
                <div>
                  <div className="mb-1.5 flex items-center justify-between font-mono text-[11px]">
                    <span className="text-muted-foreground">Spent, from the ledger</span>
                    <span className="text-foreground/80" data-testid={`program-spend-${p.code}`}>
                      {money(p.spent_ledger ?? p.spent, "INR", true)} / {money(p.budget, "INR", true)}
                    </span>
                  </div>
                  <Progress value={Math.min(((p.spent_ledger ?? p.spent) / (p.budget || 1)) * 100, 100)}
                            className="h-1.5 bg-muted" />
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="text-muted-foreground">
                      Manual expenditure claim:{" "}
                      <span className="font-mono text-foreground/70"
                            data-testid={`program-manual-${p.code}`}>
                        {money(p.spent_manual || 0, "INR", true)}
                      </span>
                    </span>
                    {p.manual_claim_ref && (
                      <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                        {p.manual_claim_ref} awaiting a vote
                      </Chip>
                    )}
                    {can("program", "write") && (
                      <Button size="sm" variant="outline"
                              className="h-6 rounded-full px-2.5 text-[10.5px]"
                              data-testid={`program-claim-button-${p.code}`}
                              onClick={() => setClaim({ code: p.code, name: p.name,
                                                        amount: p.spent_manual || 0, reason: "",
                                                        fund_id: "" })}>
                        Send for approval
                      </Button>
                    )}
                    {p.work_id && (
                      <Link to="/operations/works" data-testid={`program-work-link-${p.code}`}
                            className="font-mono text-[10.5px] text-primary hover:underline">
                        {p.work_id}
                      </Link>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {(p.projects || []).map((pr) => (
                    <div key={pr.name} className="rounded border border-border px-2.5 py-1.5">
                      <div className="text-[11px] text-foreground/80">{pr.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {money(pr.budget, "INR", true)} · {pr.milestone}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="font-mono text-[10px] text-muted-foreground/70">
                  {shortDate(p.start_date)} → {shortDate(p.end_date)}
                </div>
              </div>

              <div className="rounded border border-border bg-muted/40">
                <div className="kicker border-b border-border px-3 py-2">MEAL indicators</div>
                <Table testId={`indicators-${p.code}`} head={<><Th>Indicator</Th><Th>Baseline</Th><Th>Target</Th><Th>Result</Th><Th /><Th /></>}>
                  {(p.indicators || []).length === 0 && <Empty colSpan={6} label="No indicators defined" />}
                  {(p.indicators || []).map((i, index) => (
                    <tr key={i.name} className="row-hover" data-testid={`indicator-${p.code}-${i.name}`}>
                      <Td className="text-[11px]">{i.name}</Td>
                      <Td className="font-mono text-[11px]">{i.baseline}</Td>
                      <Td className="font-mono text-[11px]">{i.target}</Td>
                      <Td className="font-mono text-[11px] text-primary">{i.result}</Td>
                      <Td>
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                          <div className="h-full bg-emerald-500/70"
                               style={{ width: `${Math.min((i.result / (i.target || 1)) * 100, 100)}%` }} />
                        </div>
                      </Td>
                      <Td className="w-[70px]">
                        {can("program", "write") && (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-6 px-1.5"
                                    data-testid={`indicator-edit-${p.code}-${index}`}
                                    onClick={() => setIndicator({ code: p.code, index, ...i })}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 px-1.5"
                                    data-testid={`indicator-delete-${p.code}-${index}`}
                                    onClick={() => removeIndicator(p.code, index)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </Td>
                    </tr>
                  ))}
                </Table>
                {can("program", "write") && (
                  <div className="border-t border-border px-3 py-2">
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]"
                            data-testid={`indicator-add-${p.code}`}
                            onClick={() => setIndicator({ code: p.code, index: null, name: "",
                                                          baseline: 0, target: 0, result: 0,
                                                          unit: "" })}>
                      <Plus className="mr-1 h-3 w-3" /> Add an indicator
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Panel>
        ))}
      </div>
      <Dialog open={Boolean(claim)} onOpenChange={(o) => !o && setClaim(null)}>
        <DialogContent className="max-w-lg border-border bg-card" data-testid="manual-claim-dialog">
          <DialogHeader>
            <DialogTitle>Manual expenditure claim · {claim?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">
            This figure cannot be traced to a ledger posting, so it is treated as a claim. On the
            approval slabs it goes to the office bearers, or to a vote of the whole committee above
            slab 3. Once signed off it posts to the ledger against this programme.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <F label="Amount">
              <Input type="number" value={claim?.amount || ""} data-testid="manual-claim-amount"
                     onChange={(e) => setClaim({ ...claim, amount: e.target.value })}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Paid from which fund">
              <Select value={claim?.fund_id || ""}
                      onValueChange={(v) => setClaim({ ...claim, fund_id: v })}>
                <SelectTrigger data-testid="manual-claim-fund" className="bg-muted/40">
                  <SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>
                  {funds.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.code} · {money(f.balance, f.currency, true)}
                    </SelectItem>))}
                </SelectContent>
              </Select>
            </F>
            <F label="What was it spent on" full>
              <Textarea rows={2} value={claim?.reason || ""} data-testid="manual-claim-reason"
                        onChange={(e) => setClaim({ ...claim, reason: e.target.value })}
                        className="bg-muted/40 text-[12px]" />
            </F>
          </div>
          <Button onClick={sendClaim} disabled={busy === "claim"} data-testid="manual-claim-submit"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "claim" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send for approval
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(indicator)} onOpenChange={(o) => !o && setIndicator(null)}>
        <DialogContent className="max-w-lg border-border bg-card" data-testid="indicator-dialog">
          <DialogHeader>
            <DialogTitle>
              {indicator?.index === null ? "Add an indicator" : "Edit indicator"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <F label="What are you measuring">
                <Input value={indicator?.name || ""} data-testid="indicator-name-input"
                       onChange={(e) => setIndicator({ ...indicator, name: e.target.value })}
                       className="bg-muted/40 text-[12px]" />
              </F>
            </div>
            <F label="Baseline">
              <Input type="number" value={indicator?.baseline ?? 0} data-testid="indicator-baseline-input"
                     onChange={(e) => setIndicator({ ...indicator, baseline: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
            </F>
            <F label="Target">
              <Input type="number" value={indicator?.target ?? 0} data-testid="indicator-target-input"
                     onChange={(e) => setIndicator({ ...indicator, target: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
            </F>
            <F label="Result so far">
              <Input type="number" value={indicator?.result ?? 0} data-testid="indicator-result-input"
                     onChange={(e) => setIndicator({ ...indicator, result: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
            </F>
            <F label="Unit (optional)">
              <Input value={indicator?.unit || ""} data-testid="indicator-unit-input"
                     onChange={(e) => setIndicator({ ...indicator, unit: e.target.value })}
                     className="bg-muted/40 text-[12px]" />
            </F>
          </div>
          <Button onClick={saveIndicator} disabled={!indicator?.name || busy === "indicator"}
                  data-testid="indicator-save-button"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "indicator" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
